import { useParams, useNavigate } from "react-router-dom";
import { useChatThread, useSendMessage, useFinalizeThread } from "../hooks/useChat.ts";
import { useContentRun } from "../hooks/useContent.ts";
import { ChatThread } from "../components/chat/ChatThread.tsx";
import { ChatInput } from "../components/chat/ChatInput.tsx";
import { Button } from "../components/ui/Button.tsx";
import { Badge } from "../components/ui/Badge.tsx";

export function Chat() {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();

  const { data: threadData, isLoading, error } = useChatThread(threadId);
  const sendMessage = useSendMessage(threadId || "");
  const finalizeThread = useFinalizeThread(threadId || "");

  // Extract thread info
  const thread = threadData?.data;
  const messages = thread?.messages ?? [];

  // Fetch the associated content run for idea context
  const { data: runData } = useContentRun(thread?.run_id);
  const run = runData?.data;
  const selectedIdea =
    thread && run?.ideas ? run.ideas[thread.idea_index] : null;

  // Determine if we're waiting for AI response
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isWaitingForAI =
    lastMessage?.role === "user" || sendMessage.isPending;

  const handleSend = (content: string) => {
    if (!threadId) return;
    sendMessage.mutate({ content });
  };

  const handleFinalize = async () => {
    if (!threadId) return;
    try {
      await finalizeThread.mutateAsync();
      if (thread?.run_id) {
        navigate(`/review/${thread.run_id}`);
      }
    } catch {
      // Error is available via finalizeThread.error
    }
  };

  // ── Loading state ──
  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  // ── Error state ──
  if (error || !thread) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <p className="mb-2 text-sm text-red-600">
          {error ? "Failed to load chat thread." : "Chat thread not found."}
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate("/dashboard")}
        >
          Back to dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      {/* ── Header: Idea context + Finalize button ── */}
      <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {selectedIdea ? (
              <>
                <h1 className="truncate text-base font-semibold text-slate-900">
                  {selectedIdea.headline}
                </h1>
                <p className="mt-0.5 text-sm text-slate-500 line-clamp-1">
                  {selectedIdea.angle}
                </p>
                {selectedIdea.target_platforms.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {selectedIdea.target_platforms.map((platform) => (
                      <Badge key={platform} variant="default">
                        {platform}
                      </Badge>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <h1 className="text-base font-semibold text-slate-900">
                Brainstorm
              </h1>
            )}
          </div>

          <Button
            variant="primary"
            size="sm"
            loading={finalizeThread.isPending}
            onClick={handleFinalize}
          >
            Finalize
          </Button>
        </div>

        {finalizeThread.isError && (
          <p className="mt-2 text-sm text-red-600">
            Failed to finalize. Please try again.
          </p>
        )}
      </div>

      {/* ── Message list (flex-grow, scrollable) ── */}
      <ChatThread messages={messages} isWaiting={isWaitingForAI} />

      {/* ── Input (sticky bottom) ── */}
      <ChatInput
        onSend={handleSend}
        disabled={sendMessage.isPending}
      />
    </div>
  );
}
